import { SearchState } from '@reducers/search';
import type { Response } from 'express';
import { HTTPStatusCode } from '@sbt-web/network/types';
import { SeoMetaPayload } from '@sbt-web/networking';
import { isBrowser } from '@sbt-web/utils';
import { registerSeoError, registerSeoTimeoutError } from '@tools/errorHelpers';
import {
  getCanonical,
  getMetaDescription,
  getTitle,
} from '@client/components/SEO/ListingHeadTags/generators';
import { HADES_PATH, WEB_API_CHANNEL } from '@shared/constants';
import { FiltersConfigState } from '@reducers/filtersConfig';
import SeoClient, { type SeoRequest } from '@client/components/SEO/SeoClient';

const buildSeoFallback = async (
  search: SearchState,
  filtersStoreState: FiltersConfigState
): Promise<SeoMetaPayload> => {
  return {
    title: getTitle(search),
    description: getMetaDescription(search),
    canonicalUrl: getCanonical(
      search,
      filtersStoreState,
      process.env.NEXT_PUBLIC_ENV_BASE_URL
    ),
    metaRobots: 'index,follow',
    // In `ListingHeading.tsx` we have a function called buildText that is used to generate the title of the page
    // It tests the truthiness of seo?.h1, so an empty string keeps it doing the same flow as before.
    h1: '',
  };
};

const seoClient = new SeoClient(
  // We have a safe fallback on the client but we don't make the call client side
  process.env.METI_BASE_URL ?? HADES_PATH,
  WEB_API_CHANNEL,
  Number.parseInt(process.env.NEXT_PUBLIC_METI_TIMEOUT_MS, 10)
);

export const getSeoDataResponseHandler = async (
  searchState: SearchState,
  filtersStoreState: FiltersConfigState,
  seoRequest: SeoRequest,
  res?: Response
): Promise<SeoMetaPayload> => {
  const metiRequest: Promise<SeoMetaPayload> = isBrowser()
    ? // When on the client, we don't need to call Meti
      buildSeoFallback(searchState, filtersStoreState)
    : seoClient
        .getSeoData(seoRequest)
        .then((response) => {
          if (response.status === HTTPStatusCode.OK) {
            return response.payload as SeoMetaPayload;
          } else if (response.status === HTTPStatusCode.RequestTimeout) {
            registerSeoTimeoutError(res, 'METI');

            return null;
          } else {
            return null;
          }
        })
        .catch((e) => {
          registerSeoError(res, 'METI', e);

          return null;
        })
        .then(
          (seoData) =>
            seoData ?? buildSeoFallback(searchState, filtersStoreState)
        )
        .then(async (seoData) => {
          // Meti appears to be returning empty canonical URLs in some cases
          if (seoData.canonicalUrl === '') {
            const canonicalUrl = getCanonical(
              searchState,
              filtersStoreState,
              process.env.NEXT_PUBLIC_ENV_BASE_URL
            );
            return {
              ...seoData,
              canonicalUrl,
            };
          }

          return seoData;
        });

  return metiRequest;
};
